import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { atom, useAtom, useAtomValue } from "jotai";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { DataProviderEnum } from "../../../../../../apiClient/generated";
import { useEmissionRecordsApiClient } from "../../../../../../hooks";
import { emissionPageStateFamily, selectionContextAtom } from "../../../state";
import { TimelineBlock } from "../base";
import { SensirionDetailPlots } from "./DetailPlots";
import { useSensirionData } from "./hooks";
import { SensirionAlertingPlot } from "./plot";

const sumValuesAtom = atom(false);
const clickContextAtom = atom<{
    x: Date;
    y: number;
}>();

interface SensirionDataTimelineBlockProps {
    siteId: string;
    sensirionSiteId: string;
    dataProvider?: DataProviderEnum;
}

export const SensirionDataTimelineBlock = (
    props: SensirionDataTimelineBlockProps,
) => {
    // API Client
    const emissionsApiClient = useEmissionRecordsApiClient();

    // Internal states
    const pageState = useAtomValue(emissionPageStateFamily(props.siteId));
    const siteFilters = useMemo(
        () => ({ dateFrom: pageState.dateFrom, dateTo: pageState.dateTo }),
        [pageState.dateFrom, pageState.dateTo],
    );
    const [sumValues, setSumValues] = useAtom(sumValuesAtom);
    const [clickContext, setClickContext] = useAtom(clickContextAtom);
    const [selectionContext] = useAtom(selectionContextAtom);
    const [hoverContext, setHoverContext] = useState<{ x: Date; y: number }>();
    const [isExpanded, setIsExpanded] = useState(false);

    // Auto-open when emission is selected
    useEffect(() => {
        if (selectionContext.emissionId) {
            setIsExpanded(true);
        }
        // Note: We only auto-open, never auto-close
        // This respects user intent if they manually close the block
    }, [selectionContext.emissionId]);

    // Debounce date range
    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore = siteFilters.dateTo;
            if (selectionContext.dateRange?.end) {
                dateBefore = selectionContext.dateRange?.end;
            }
            let dateAfter = siteFilters.dateFrom;
            if (selectionContext.dateRange?.start) {
                dateAfter = selectionContext.dateRange.start;
            }
            return { dateBefore, dateAfter };
        }, [
            siteFilters.dateFrom,
            siteFilters.dateTo,
            selectionContext.dateRange,
        ]),
        100,
    );

    // Load emission info if selected
    const emissionRecordQuery = useQuery({
        queryKey: ["emissionRecord", selectionContext.emissionId],
        queryFn: async () => {
            const response = await emissionsApiClient.emissionRecordsRetrieve({
                id: selectionContext.emissionId,
            });
            return response;
        },
        enabled: !!selectionContext.emissionId,
    });

    const dateRangeToLoad = useMemo(() => {
        // If emission is selected, prioritize it with ±7 days
        if (selectionContext.emissionId) {
            // Wait for emission data to load before determining date range
            if (!emissionRecordQuery.data) {
                return;
            }
            const emissionTimestamp = DateTime.fromJSDate(
                emissionRecordQuery.data.dataPoint.detectionTimestamp,
            );
            return {
                start: emissionTimestamp.minus({ days: 7 }).toJSDate(),
                end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
            };
        }

        // No emission selected - use date filters but limit to 14 days max
        const start = DateTime.fromJSDate(debouncedDateRange.dateAfter);
        const end = DateTime.fromJSDate(debouncedDateRange.dateBefore);

        // Calculate the duration
        const durationInDays = end.diff(start, "days").days;

        // If the range is more than 14 days, take the most recent 14 days
        if (durationInDays > 14) {
            return;
        }

        return {
            start: start.toJSDate(),
            end: end.toJSDate(),
        };
    }, [
        selectionContext.emissionId,
        emissionRecordQuery.data,
        debouncedDateRange,
    ]);

    const sensorData = useSensirionData({
        siteId: dateRangeToLoad ? props.siteId : undefined,
        startDate: dateRangeToLoad?.start,
        endDate: dateRangeToLoad?.end,
        dataType: "alerts",
        dataProvider: props.dataProvider,
    });

    const sensorPreviews = useMemo(() => {
        if (sensorData.isLoading) {
            return [];
        }
        const allMeasurements = [];

        // Only return dates where rate > 10kg/h
        for (const d of sensorData.data) {
            d.rates.forEach((r) => {
                if (r.measurement > 10) {
                    allMeasurements.push(r.timestamp);
                }
            });
        }
        return allMeasurements;
    }, [sensorData]);

    const dateFrom = DateTime.fromJSDate(clickContext?.x)
        .toUTC()
        .minus({ hours: 12 });
    const dateTo = DateTime.fromJSDate(clickContext?.x)
        .toUTC()
        .plus({ hours: 12 });

    const dataProviderLabel =
        props.dataProvider.charAt(0).toUpperCase() +
        props.dataProvider.slice(1);

    return (
        <TimelineBlock
            open={isExpanded}
            onOpenChange={setIsExpanded}
            initiallyOpen={false}
            message={
                !dateRangeToLoad ? (
                    <div className="flex items-center justify-center gap-2">
                        Select an emission to load sensor data (± 7 days) or
                        pick a date range &lt; 14 days wide
                        <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                ) : undefined
            }
            title={
                props.dataProvider === DataProviderEnum.Longpath
                    ? "LongPath"
                    : "Sensirion (Nubo Sphere)"
            }
            loading={dateRangeToLoad && sensorData.isLoading}
            previewPoints={sensorPreviews || []}
            dataDateRange={
                selectionContext.dateRange
                    ? selectionContext.dateRange
                    : {
                          start: siteFilters.dateFrom,
                          end: siteFilters.dateTo,
                      }
            }
        >
            <div className="flex min-h-64">
                <div className="p-1 h-full w-[200px]">
                    <div className="my-2 flex flex-col gap-1">
                        <label className="flex items-center gap-1">
                            <input
                                type="checkbox"
                                className="rounded text-ae-blue-550"
                                checked={sumValues}
                                onClick={() => setSumValues(!sumValues)}
                            />
                            Stack Values
                        </label>
                    </div>

                    <div className="flex flex-col gap-0.5">
                        <p className="text-neutral-500">Plot data</p>
                        <p>
                            Showing {dataProviderLabel} Alerting data. Timeline
                            previews shows points {">"} 10kg/h.
                        </p>
                        <p>
                            {selectionContext.emissionId
                                ? "Showing ± 7 days around selected emission."
                                : "Showing up to 14 days from date filters."}
                        </p>
                        {!clickContext && (
                            <p>
                                Select a date range (highlighted) to load sensor
                                data.
                            </p>
                        )}
                    </div>

                    {clickContext && (
                        <div className="flex flex-col gap-0.5 mt-2">
                            <p className="text-neutral-500">Selection</p>
                            <p>
                                From:{" "}
                                {dateFrom.toFormat("MMM dd, yyyy, h:mm:ss a")}{" "}
                                UTC
                            </p>
                            <p>
                                To: {dateTo.toFormat("MMM dd, yyyy, h:mm:ss a")}{" "}
                                UTC
                            </p>
                        </div>
                    )}

                    {hoverContext && (
                        <div className="mt-2 text-xs text-ae-slate-600">
                            Cursor at:
                            <br />
                            Date:{" "}
                            {DateTime.fromJSDate(hoverContext.x)
                                .toUTC()
                                .toFormat("MMM dd, yyyy, h:mm:ss a")}{" "}
                            UTC
                            <br />
                            Rate: {hoverContext.y.toFixed(2)} kg/h
                        </div>
                    )}
                </div>
                <div className="flex-1 relative">
                    <SensirionAlertingPlot
                        data={sensorData.data}
                        dataDateRange={debouncedDateRange}
                        setHoverContext={setHoverContext}
                        sumPlots={sumValues}
                        clickContext={clickContext}
                        setClickContext={setClickContext}
                    />
                </div>
            </div>

            {/* This is the area destined to load the sensirion plots. */}
            {clickContext &&
                props.dataProvider === DataProviderEnum.Sensirion && (
                    <SensirionDetailPlots
                        siteId={props.siteId}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                    />
                )}
        </TimelineBlock>
    );
};
